import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Checkbox } from '../../../../../components/forms/checkbox/checkbox.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Archive, Plus, RefreshCcw, X } from '../../../../../components/icon/solar-icons.js'
import { WorkingPatternDayGrid } from '../../../../../components/working-pattern-day-grid/working-pattern-day-grid.js'
import {
    archiveSettingsWorkingPattern,
    createSettingsWorkingPattern,
    fetchSettingsWorkingPatterns,
    restoreSettingsWorkingPattern,
    type SettingsWorkingPattern,
    updateSettingsWorkingPattern,
} from '../../../utils/portal-api.js'

export interface WorkingPatternsSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

interface PatternForm {
    name: string
    summary: string
    days: number[] // length 7, Mon-first
    standard_day_hours: number
    full_time_hours: number
    /** Raw manual-FTE text — kept as typed so partial decimals ("0," / "0.")
     *  survive the controlled round-trip; parsed by parseFte(). */
    fte: string
    fteOverride: boolean
}

const emptyForm: PatternForm = {
    name: '',
    summary: '',
    days: [8, 8, 8, 8, 8, 0, 0],
    standard_day_hours: 8,
    full_time_hours: 40,
    fte: '1',
    fteOverride: false,
}

/** Parse the manual-FTE text; comma and dot both accepted. Null when unusable. */
const parseFte = (raw: string): number | null => {
    const n = Number(raw.replace(',', '.'))
    return Number.isFinite(n) && n > 0 && n <= 1 ? n : null
}

const parsePatternArray = (raw: string): (number | boolean)[] => {
    try {
        const parsed = JSON.parse(raw) as unknown
        if (Array.isArray(parsed)) return parsed as (number | boolean)[]
        return []
    } catch {
        return []
    }
}

const renderPatternSummary = (
    raw: string,
    dayLabels: {
        mon: string
        tue: string
        wed: string
        thu: string
        fri: string
        sat: string
        sun: string
    },
): string => {
    const arr = parsePatternArray(raw)
    if (arr.length === 0) return '—'
    const labels = [
        dayLabels.mon,
        dayLabels.tue,
        dayLabels.wed,
        dayLabels.thu,
        dayLabels.fri,
        dayLabels.sat,
        dayLabels.sun,
    ]
    return arr
        .slice(0, 7)
        .map((slot, i) => {
            if (typeof slot === 'number') return slot > 0 ? `${labels[i]}${slot}` : `${labels[i]}—`
            return slot ? `${labels[i]}✓` : `${labels[i]}—`
        })
        .join(' ')
}

export const WorkingPatternsSection = ({ lang, showToast }: WorkingPatternsSectionProps) => {
    const s = t.portal.settings.workingPatterns
    const dayLabels = {
        mon: s.day.mon(lang),
        tue: s.day.tue(lang),
        wed: s.day.wed(lang),
        thu: s.day.thu(lang),
        fri: s.day.fri(lang),
        sat: s.day.sat(lang),
        sun: s.day.sun(lang),
    }

    const [patterns, setPatterns] = useState<SettingsWorkingPattern[]>([])
    const [showArchived, setShowArchived] = useState(false)
    const [slideOpen, setSlideOpen] = useState(false)
    const [editing, setEditing] = useState<SettingsWorkingPattern | null>(null)
    const [form, setForm] = useState<PatternForm>(emptyForm)
    const [saving, setSaving] = useState(false)

    const reload = async () => {
        const r = await fetchSettingsWorkingPatterns(showArchived)
        if (r.ok) setPatterns(r.data)
    }

    useEffect(() => {
        void reload()
    }, [showArchived])

    const workingHoursFromDays = (days: number[]) =>
        days.reduce((acc, d) => acc + (Number.isFinite(d) ? d : 0), 0)

    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setSlideOpen(true)
    }

    const openEdit = (p: SettingsWorkingPattern) => {
        const arr = parsePatternArray(p.working_pattern)
        const days = Array.from({ length: 7 }, (_, i) => {
            const slot = arr[i]
            if (typeof slot === 'number') return slot
            if (typeof slot === 'boolean') return slot ? Number(p.standard_day_hours ?? 8) : 0
            return 0
        })
        setEditing(p)
        setForm({
            name: p.name,
            summary: p.summary ?? '',
            days,
            standard_day_hours: Number(p.standard_day_hours ?? 8),
            full_time_hours: Number(p.full_time_hours ?? 40),
            fte: String(p.fte ?? 1),
            fteOverride: true,
        })
        setSlideOpen(true)
    }

    const close = () => {
        setSlideOpen(false)
        setEditing(null)
    }

    // Auto FTE from the schedule; an unusable manual entry falls back to it.
    const autoFte = () => {
        if (form.full_time_hours <= 0) return 1
        return Math.min(1, workingHoursFromDays(form.days) / form.full_time_hours)
    }

    const computedFte = () => {
        if (form.fteOverride) return parseFte(form.fte) ?? autoFte()
        return autoFte()
    }

    const save = async () => {
        if (!form.name) return
        setSaving(true)
        try {
            const payload = {
                name: form.name,
                summary: form.summary || undefined,
                working_pattern: form.days,
                standard_day_hours: form.standard_day_hours,
                full_time_hours: form.full_time_hours,
                fte: computedFte(),
            }
            if (editing) {
                const r = await updateSettingsWorkingPattern(editing.id, payload)
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.updated(lang), 'success')
            } else {
                const r = await createSettingsWorkingPattern(payload)
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.created(lang), 'success')
            }
            close()
            await reload()
        } finally {
            setSaving(false)
        }
    }

    const handleArchive = async (p: SettingsWorkingPattern) => {
        const r = await archiveSettingsWorkingPattern(p.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.archived_msg(lang), 'success')
        await reload()
    }

    const handleRestore = async (p: SettingsWorkingPattern) => {
        const r = await restoreSettingsWorkingPattern(p.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.restored(lang), 'success')
        await reload()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <div className="portal-settings__header-controls">
                    <Checkbox
                        size="small"
                        label={s.showArchived(lang)}
                        isChecked={showArchived}
                        onChange={(isChecked) => setShowArchived(isChecked)}
                    />
                    <Button
                        variant="primary"
                        size="small"
                        iconLeft={Plus}
                        text={s.createPattern(lang)}
                        onClick={openCreate}
                    />
                </div>
            </div>

            {patterns.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noPatterns(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {patterns.map((p) => (
                        <div
                            key={p.id}
                            className="portal-settings__data-row"
                            style={p.is_archived ? { opacity: 0.55 } : undefined}
                        >
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">
                                    {p.name}
                                    {p.is_archived ? (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontSize: '0.75rem',
                                                color: 'var(--portal-text-secondary)',
                                            }}
                                        >
                                            ({s.archived(lang)})
                                        </span>
                                    ) : null}
                                    {p.humaans_id ? (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontSize: '0.75rem',
                                                color: 'var(--portal-text-secondary)',
                                            }}
                                            title={s.imported(lang)}
                                        >
                                            ⤓
                                        </span>
                                    ) : null}
                                </div>
                                <div className="portal-settings__data-meta">
                                    {renderPatternSummary(p.working_pattern, dayLabels)} ·{' '}
                                    {p.working_hours ?? 0}h · FTE {p.fte ?? 1} ·{' '}
                                    {s.allocationsCount(lang)}: {p.active_allocations_count}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                {p.is_archived ? (
                                    <button type="button" onClick={() => void handleRestore(p)}>
                                        <RefreshCcw
                                            size={12}
                                            style={{ verticalAlign: 'middle', marginRight: 4 }}
                                        />
                                        {s.restore(lang)}
                                    </button>
                                ) : (
                                    <>
                                        <button type="button" onClick={() => openEdit(p)}>
                                            {s.edit(lang)}
                                        </button>
                                        <button
                                            type="button"
                                            className="is-danger"
                                            onClick={() => void handleArchive(p)}
                                        >
                                            <Archive
                                                size={12}
                                                style={{
                                                    verticalAlign: 'middle',
                                                    marginRight: 4,
                                                }}
                                            />
                                            {s.archive(lang)}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={close} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editing ? s.editPattern(lang) : s.createPattern(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.name(lang)}
                                    value={form.name}
                                    onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.summary(lang)}
                                    value={form.summary}
                                    onChange={(value) => setForm((f) => ({ ...f, summary: value }))}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <label>{s.weeklySchedule(lang)}</label>
                                <WorkingPatternDayGrid
                                    mode="fixed"
                                    days={form.days}
                                    onChange={(days) => setForm((f) => ({ ...f, days }))}
                                    dayLabels={dayLabels}
                                />
                                <small
                                    style={{
                                        color: 'var(--portal-text-secondary)',
                                    }}
                                >
                                    {s.weeklyScheduleHint(lang)} · {s.workingHours(lang)}:{' '}
                                    {workingHoursFromDays(form.days)}h
                                </small>
                            </div>

                            <div
                                className="portal-slide-over__field"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 12,
                                    // One label can wrap to two lines — keep the
                                    // input boxes on a shared baseline.
                                    alignItems: 'end',
                                }}
                            >
                                <div>
                                    <TextField
                                        label={s.standardDayHours(lang)}
                                        inputProps={{ type: 'number', min: 0, max: 24, step: 0.5 }}
                                        value={String(form.standard_day_hours)}
                                        onChange={(value) =>
                                            setForm((f) => ({
                                                ...f,
                                                standard_day_hours: Number(value),
                                            }))
                                        }
                                    />
                                </div>
                                <div>
                                    <TextField
                                        label={s.fullTimeHours(lang)}
                                        inputProps={{ type: 'number', min: 0, max: 168, step: 0.5 }}
                                        value={String(form.full_time_hours)}
                                        onChange={(value) =>
                                            setForm((f) => ({
                                                ...f,
                                                full_time_hours: Number(value),
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            {/* Separate fields so the checkbox and the input get the
                                slide-over body's full 16px breathing room. */}
                            <div className="portal-slide-over__field">
                                <Checkbox
                                    size="small"
                                    label={s.fteOverride(lang)}
                                    isChecked={form.fteOverride}
                                    onChange={(isChecked) =>
                                        setForm((f) => ({
                                            ...f,
                                            fteOverride: isChecked,
                                        }))
                                    }
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.fte(lang)}
                                    inputProps={{ inputMode: 'decimal' }}
                                    value={form.fteOverride ? form.fte : String(computedFte())}
                                    isDisabled={!form.fteOverride}
                                    onChange={(value) => setForm((f) => ({ ...f, fte: value }))}
                                    note={form.fteOverride ? undefined : { text: s.fteAuto(lang) }}
                                />
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !form.name}
                                onClick={() => void save()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
